/**
 * Error Handling Utilities
 * Transform technical errors into user-friendly messages
 */

// Get user-friendly error message
export const getUserFriendlyError = (error) => {
  if (!error) return 'An unexpected error occurred';

  // Supabase/PostgreSQL error codes
  if (error.code) {
    switch (error.code) {
      case 'PGRST116':
        return 'Unable to connect to the server. Please check your internet connection.';
      case 'PGRST301':
        return 'Not found. The requested resource does not exist.';
      case '23505':
        return 'This record already exists.';
      case '23503':
        return 'Cannot delete this record as it is referenced by other data.';
      case '42P01':
        return 'Database table not found. Please contact support.';
      default:
        break;
    }
  }

  // JWT/Auth errors
  if (error.message) {
    const msg = error.message.toLowerCase();
    
    if (msg.includes('jwt') || msg.includes('token')) {
      return 'Your session has expired. Please log in again.';
    }
    
    if (msg.includes('network') || msg.includes('fetch')) {
      return 'Network error. Please check your internet connection.';
    }
    
    if (msg.includes('permission') || msg.includes('unauthorized')) {
      return 'You do not have permission to perform this action.';
    }
    
    if (msg.includes('not found')) {
      return 'The requested resource was not found.';
    }
    
    if (msg.includes('invalid') && msg.includes('credentials')) {
      return 'Invalid email or password.';
    }
    
    if (msg.includes('email not confirmed')) {
      return 'Please check your email and confirm your account.';
    }
    
    if (msg.includes('already registered') || msg.includes('already exists')) {
      return 'An account with this email already exists.';
    }
  }

  // Return the original error message if no friendly version found
  return error.message || error.toString() || 'An unexpected error occurred';
};

// Handle async operation with error handling
export const handleAsyncOperation = async (operation, options = {}) => {
  const {
    successMessage,
    errorMessage,
    onSuccess,
    onError,
    showToast = true,
  } = options;

  try {
    const result = await operation();
    
    if (showToast && successMessage) {
      // Toast will be handled by the component using react-hot-toast
      if (import.meta.env.DEV) console.log('Success:', successMessage);
    }
    
    if (onSuccess) {
      onSuccess(result);
    }
    
    return { data: result, error: null };
  } catch (error) {
    console.error('Operation error:', error);
    
    const friendlyError = getUserFriendlyError(error);
    
    if (showToast && (errorMessage || friendlyError)) {
      console.error('Error:', errorMessage || friendlyError);
    }
    
    if (onError) {
      onError(error);
    }
    
    return { data: null, error: friendlyError };
  }
};

// Validate form fields
export const validateField = (name, value, rules = {}) => {
  const { required, minLength, maxLength, pattern, custom } = rules;

  if (required && !value) {
    return `${name} is required`;
  }

  if (minLength && value.length < minLength) {
    return `${name} must be at least ${minLength} characters`;
  }

  if (maxLength && value.length > maxLength) {
    return `${name} must be at most ${maxLength} characters`;
  }

  if (pattern && !pattern.test(value)) {
    return rules.patternMessage || `${name} format is invalid`;
  }

  if (custom && typeof custom === 'function') {
    const customError = custom(value);
    if (customError) return customError;
  }

  return null;
};

// Email validation
export const validateEmail = (email) => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return validateField('Email', email, {
    required: true,
    pattern: emailPattern,
    patternMessage: 'Please enter a valid email address',
  });
};

// Password validation
export const validatePassword = (password) => {
  if (!password) return 'Password is required';
  if (password.length < 6) return 'Password must be at least 6 characters';
  if (!/(?=.*[a-z])/.test(password)) return 'Password must contain at least one lowercase letter';
  if (!/(?=.*[A-Z])/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/(?=.*\d)/.test(password)) return 'Password must contain at least one number';
  return null;
};

// Phone validation
export const validatePhone = (phone) => {
  const phonePattern = /^\+?[\d\s-()]+$/;
  return validateField('Phone', phone, {
    pattern: phonePattern,
    patternMessage: 'Please enter a valid phone number',
  });
};

// Log error to console (can be extended to send to error tracking service)
export const logError = (error, context = {}) => {
  console.error('Error occurred:', {
    error: error.message || error,
    stack: error.stack,
    context,
    timestamp: new Date().toISOString(),
  });
  
  // TODO: Send to error tracking service (e.g., Sentry)
};

export default {
  getUserFriendlyError,
  handleAsyncOperation,
  validateField,
  validateEmail,
  validatePassword,
  validatePhone,
  logError,
};
